<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import webapp from '@/services/webapp'
import { initialize } from '@/composables/init'
import TheLayout from '@/TheLayout.vue'
import TheInitLoading from '@/components/TheInitLoading.vue'
import { TolgeeProvider } from '@tolgee/vue'
import TheQRCode from '@/components/TheQRCode.vue'
import { useAuth } from '@/composables/auth'
import { useAnalytics } from '@/composables/analytics'
import { TonConnectUIProvider } from '@townsquarelabs/ui-vue'
import { env } from '@/env'
import { sleep } from '@/services/helper'
import { DateTime } from 'luxon'
import { useNow } from '@/composables/game/time'
import { useNewLoader } from '@/config/new-loader'
import router from '@/router'
import { RouteName } from '@/routes'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { currentTimestamp } from '@/helpers/date'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { useRoute } from 'vue-router'
import GameResultLoading from '@/components/GameResultLoading.vue'
import { isiOS } from '@/helpers/browser'
import TempInBetweenScreen from '@/components/TempInBetweenScreen.vue'

const route = useRoute()
const isReturningFromMiniGame = computed(() => !!route.query.gameSubmitted)
const isInitialized = ref(false)
const { setUserId } = useAnalytics()
const { topNumber } = useFullScreenDimensions()
const { now } = useNow()

const inBetweenScreenTimestamp = DateTime.utc(2026, 1, 6, 8, 30, 0).toSeconds()
const lastInitTimestamp = useLocalStorage<number>(buildDropeeStorageKey('lastInitTimestamp'), 0)
const showInBetweenScreen = computed(() => now.value >= inBetweenScreenTimestamp)
const topStyles = computed(() => {
  return { paddingTop: isInitialized.value ? `${topNumber.value}px` : `0px` }
})

async function _initialize() {
  if (!useNewLoader) {
    await initialize()
  }
  const { profile } = useAuth()
  setUserId(String(profile.value.id))
}

onMounted(async () => {
  try {
    webapp.requestFullscreen()
  } catch {
    await webapp.showAlert('Please update Telegram')
  }
  webapp.expand()
  webapp.ready()

  // // @ts-ignore
  // window.TelegramAdsController = new TelegramAdsController()
  // // @ts-ignore
  // window.TelegramAdsController.initialize({
  //   pubId: '968536',
  //   appId: '2054',
  // })

  if (webapp.isNotMobile) return

  await Promise.all([
    _initialize(),
    sleep(isReturningFromMiniGame.value ? 1000 : 3000),
  ])
  lastInitTimestamp.value = currentTimestamp()
  isInitialized.value = true
})

function preventCollapse() {
  if (window.scrollY === 0) {
    window.scrollTo(0, 1)
  }
}

onMounted(() => {
  const adSonar = document.createElement('script')
  adSonar.setAttribute('src', 'https://static.sonartech.io/lib/1.0.0/sonar.js?appId=app_8a8db56d')
  document.head.appendChild(adSonar)
})
</script>

<template>
  <TonConnectUIProvider
    :options="{ manifestUrl: `https://webapp.game.dropee.xyz/tonconnect-manifest${env.app.isProd ? '' : '-dev'}.json` }"
  >
    <TolgeeProvider>
      <div
        class="text-white bg-dark-primary max-w-[1023px] my-0 mx-auto w-screen h-screen outer-scroll-container relative"
      >
        <video
          class="video-background"
          autoplay
          loop
          muted
          playsinline
        >
          <source
            v-if="!isiOS()"
            src="@/assets/videos/periodic/snowing.webm"
            type="video/webm"
          >
          <source
            v-else
            src="@/assets/videos/periodic/snowing.mp4"
            type="video/mp4"
          >
        </video>
        <div
          class="relative z-[1] overflow-hidden w-full h-screen outer-scroll-container"
        >
          <div
            id="app-wrapper"
            @touchstart="preventCollapse"
            class="w-full scroll-container outer-scroll-container flex flex-col h-screen"
            :class="router.currentRoute.value.name !== RouteName.HOME && 'overflow-auto'"
            :style="isInitialized && !showInBetweenScreen ? topStyles : undefined"
          >
            <div id="adsonar_all" />
            <TheQRCode
              v-if="webapp.isNotMobile"
              class="text-black"
            />
            <TempInBetweenScreen v-else-if="showInBetweenScreen" />
            <template v-else-if="!isInitialized">
              <TheInitLoading v-if="!isReturningFromMiniGame" />
              <GameResultLoading v-else />
            </template>
            <TheLayout
              v-else
            />
          </div>
        </div>
      </div>
    </TolgeeProvider>
  </TonConnectUIProvider>
</template>

<style scoped>
#app-fixed-bg {
	background-image: url('@/assets/images/app-background.png');
	background-size: cover;
	background-repeat: no-repeat;
	background-blend-mode: overlay;
}

.outer-scroll-container {
	overscroll-behavior: none;
}

.video-background {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	z-index: 0;
	pointer-events: none;
}
</style>
